<script setup lang="ts">
  import L, { LatLngExpression } from 'leaflet'
  import 'leaflet-draw'
  import 'leaflet/dist/leaflet.css'
  import 'leaflet-draw/dist/leaflet.draw.css'
  import 'leaflet-draw/dist/leaflet.draw-src.js'
  import 'leaflet-contextmenu'
  import 'leaflet-contextmenu/dist/leaflet.contextmenu.min.css'
  import { LMap, LTileLayer } from '@vue-leaflet/vue-leaflet'
  import MarkerWrapper from './MarkerWrapper.vue'
  import PolylineWrapper from '@/components/projectDetail/GTLPMap/PolylineWrapper.vue'
  import PolygonWrapper from '@/components/projectDetail/GTLPMap/PolygonWrapper.vue'
  import type { SectionLine } from '@/stores/type/sectionLine.type'
  import { useDrawerWidth } from '@/composables/map/useDrawerWidth'
  import { useEventsBus } from '@haskoning/vue-component-library'
  import { useSectionLineUploadCollectionsStore } from '@/stores/sectionLineUploadCollections'
  import WelcomePopup from '@/components/projectDetail/GTLPMap/WelcomePopup.vue'
  import type { GroundInvestigationPoint } from '@/stores/type/giPoint.type'
  import type { SectionSelectionPolygon } from '@/stores/type/selectionPolygon.type'
  import { findSelectionGIPointsIDs } from '@/utils/map'
  import { storeToRefs } from 'pinia'
  import { watch } from 'vue'
  import ControlPopup from '@/components/projectDetail/GTLPMap/ControlPopup.vue'

  L.drawLocal.draw.handlers.polyline.tooltip = {
    start: 'Click to start drawing the section line.',
    cont: 'Click to continue drawing the section line.',
    end: 'Double-click to finish this section line.'
  }
  L.drawLocal.draw.handlers.polygon.tooltip = {
    start: 'Click to start drawing the polygon.',
    cont: 'Click to continue drawing the polygon.',
    end: 'Click first point to close this polygon.'
  }

  const store = useProjectStore()
  const giPointsStore = useGIPointsStore()
  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const sectionLinesStore = useSectionLinesStore()
  const selectionPolygonsStore = useSelectionPolygonsStores()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
  const { items: sectionLineUploadCollections } = storeToRefs(sectionLineUploadCollectionsStore)
  const { projectLoading, lengthProfileGraphBar, isLengthProfileGraphBarShown } = storeToRefs(store)
  const { hasCoordinatesGiPoints } = storeToRefs(giPointsStore)
  const { items: polygons } = storeToRefs(selectionPolygonsStore)
  const { currentItem: currentSectionLine, selectedItems: selectedSectionLines } =
    storeToRefs(sectionLinesStore)
  const { newPolygonCoordinates, polygonsBySectionLineId } = storeToRefs(selectionPolygonsStore)
  const { giPointsBySectionLineId } = storeToRefs(sectionLinesGIPointsStore)
  const map = ref()
  const container = ref<HTMLElement | null>(null)
  const { bus, emit } = useEventsBus()
  const { drawerWidth } = useDrawerWidth()
  const RECENTER_MAP_DELAY_MS = 500

  const mapContainerStyle = computed(() => {
    return {
      height: isLengthProfileGraphBarShown.value
        ? `calc(100% - ${lengthProfileGraphBar.value.height}px)`
        : '100%',
      width: `calc(100% - ${drawerWidth.value}px)`,
      marginLeft: `${drawerWidth.value}px`
    }
  })

  const debounceInvalidateSize = () => {
    setTimeout(() => {
      map.value?.leafletObject.invalidateSize(true)
    }, RECENTER_MAP_DELAY_MS)
  }

  const onLeafletReady = async () => {
    map.value.leafletObject.on(L.Draw.Event.CREATED, handleCreatedEvent)
    const sectionLinePane = map.value.leafletObject.createPane('sectionLinePane')
    sectionLinePane.style.zIndex = 800
  }

  async function handleCreatedEvent(e: L.DrawEvents.Created) {
    const handlers = [handleSectionLineCreated, handlePolygonCreated]
    for (const handler of handlers) {
      await handler(e)
    }
  }

  const handleSectionLineCreated = async (e: L.DrawEvents.Created) => {
    const layer = e.layer
    if (e.layerType !== 'polyline' || !(layer instanceof L.Polyline)) {
      return
    }
    const geoJsonPolyline = layer.toGeoJSON().geometry
    sectionLinesStore.setSectionLineCoordinates(geoJsonPolyline)
    sectionLinesStore.toggleUpdateSectionLineDialog('create')
  }

  const createPolygon = async () => {
    const createObj = {
      sectionline: selectedSectionLines?.value[0]?.id, // for permission check
      section_line: selectedSectionLines?.value[0]?.id,
      zone_name: 'New Polygon',
      polygon_geom: newPolygonCoordinates.value || ''
    }
    await selectionPolygonsStore.create(createObj)
  }

  const addSelectionByPolygons = (
    sectionLine: SectionLine,
    polygons: SectionSelectionPolygon[],
    giPointsArr: GroundInvestigationPoint[]
  ) => {
    const selectedGIPointsIDs = findSelectionGIPointsIDs(polygons, giPointsArr)
    const existingGIPointsIDs = giPointsBySectionLineId
      .value(sectionLine.id)
      .map((point: GroundInvestigationPoint) => point.id)
    const newGIPointsIDs = selectedGIPointsIDs.filter((id) => !existingGIPointsIDs.includes(id))
    if (selectedGIPointsIDs.length > 0) {
      sectionLinesGIPointsStore.addGIPointsToSectionLine(newGIPointsIDs, sectionLine.id)
    }
  }

  const handlePolygonCreated = async (e: L.DrawEvents.Created) => {
    const layer = e.layer
    if (e.layerType !== 'polygon' || !(layer instanceof L.Polygon)) {
      return
    }

    selectionPolygonsStore.setPolygonCoordinates(layer.toGeoJSON().geometry)
    await createPolygon()
    addSelectionByPolygons(
      selectedSectionLines?.value[0],
      polygonsBySectionLineId.value(selectedSectionLines?.value[0].id),
      hasCoordinatesGiPoints.value()
    )
  }

  const flyToMarker = (markerCoordinates: number[]) => {
    const coordinates = [...markerCoordinates]
    map.value.leafletObject.flyTo([coordinates[1], coordinates[0]], 18)
  }

  const polylinesByCollection = (collectionArr: SectionLine[]) => {
    const polylineArr: SectionLine[] = []
    collectionArr.forEach((item: SectionLine) => {
      polylineArr.push(...sectionLinesStore.sectionLineByCollectionId(item.id))
    })
    return polylineArr
  }

  onMounted(async () => {
    // recenter the map when the container size changes
    await nextTick()
    useResizeObserver(container, debounceInvalidateSize)
  })

  watch(
    () => bus.value.get('project-loaded'),
    () => {
      const firstSectionLineCollection = sectionLineUploadCollections.value[0]
      if (firstSectionLineCollection) {
        const sectionLineSet = firstSectionLineCollection.sectionline_set.features
        const coords = sectionLineSet.map((sectionLine: SectionLine) => {
          return sectionLine.geometry.coordinates.map((coordinate: LatLngExpression[]) => {
            return [coordinate[1], coordinate[0]]
          })
        })
        if (coords.length > 0) map.value.leafletObject.flyToBounds(coords)
      }
    }
  )

  watch(
    () => bus.value.get('fly-to-polyline'),
    ([payloadPolyline]) => {
      const reversedCoords = payloadPolyline.geometry.coordinates.map(
        (coordinate: LatLngExpression[]) => [coordinate[1], coordinate[0]]
      )
      map.value.leafletObject.flyToBounds(reversedCoords)
    }
  )
  watch(
    () => bus.value.get('fly-to-gi-collection'),
    ([payloadCollection]) => {
      const latLngs = payloadCollection.gipoint_set.features.map(
        (feature: GroundInvestigationPoint) =>
          L.latLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0])
      )

      map.value.leafletObject.flyToBounds(latLngs)
    }
  )

  watch(
    () => bus.value.get('fly-to-collection'),
    ([payloadCollection]) => {
      const sectionLineSet = payloadCollection.sectionline_set.features
      const coords = sectionLineSet.map((sectionLine: SectionLine) => {
        return sectionLine.geometry.coordinates.map((coordinate: LatLngExpression[]) => {
          return [coordinate[1], coordinate[0]]
        })
      })
      map.value.leafletObject.fitBounds(coords)
    }
  )
  watch(
    () => bus.value.get('draw-polygon'),
    () => {
      new L.Draw.Polygon(map.value.leafletObject).enable()
    }
  )
  watch(
    () => bus.value.get('draw-section-line'),
    () => {
      new L.Draw.Polyline(map.value.leafletObject).enable()
    }
  )
  const hoverOnSectionLine = (e) => {
    if (!lengthProfileGraphBar.value?.shown) return
    emit('mouse-position', [e.latlng.lng, e.latlng.lat, 0])
  }
</script>

<template>
  <div ref="container" :style="mapContainerStyle">
    <v-progress-linear
      v-if="projectLoading"
      indeterminate
      top
      color="secondary"
    ></v-progress-linear>
    <l-map
      ref="map"
      :zoom="8"
      :center="[52.1561, 5.3878]"
      @ready="onLeafletReady"
      :min-zoom="4"
      :max-zoom="20"
      :options="{
        doubleClickZoom: false,
        contextmenu: true,
        preferCanvas: true
      }"
      :use-global-leaflet="true"
    >
      <WelcomePopup />
      <ControlPopup />
      <l-tile-layer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
        attribution="GeoTechnical Profile"
        :layer-type="'base'"
        :options="{
          maxNativeZoom: 19,
          maxZoom: 20
        }"
      ></l-tile-layer>
      <PolygonWrapper v-for="polygon in polygons" :item="polygon" :key="`polygon-${polygon.id}`" />
      <MarkerWrapper
        @fly-to-marker="flyToMarker"
        v-for="marker in hasCoordinatesGiPoints()"
        :item="marker"
        :key="`marker-${marker.id}`"
        :map="map"
      />
      <PolylineWrapper
        v-for="polyline in polylinesByCollection(sectionLineUploadCollections)"
        @mousemove="hoverOnSectionLine"
        :item="polyline"
        :key="`polyline-${polyline.id}`"
        :current-item="currentSectionLine"
      />
    </l-map>
  </div>
</template>

<style>
  * {
    outline: none;
  }
</style>
